import React, { useState } from 'react';
import { Company, CompanyFormData, CompanyRoleType } from '../../types/models';
import { CompanyService } from '../../services/companyService';
import { EmployeeAutocomplete } from '../EmployeeAutocomplete';

interface CompanyFormProps {
  company?: Company;
  onSuccess: () => void;
  onCancel: () => void;
}

export const CompanyForm: React.FC<CompanyFormProps> = ({ company, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState<CompanyFormData>({
    fullName: company?.fullName || '',
    shortName: company?.shortName || '',
    inn: company?.inn || '',
    ogrn: company?.ogrn || '',
    kpp: company?.kpp || '',
    legalAddress: company?.legalAddress || '',
    actualAddress: company?.actualAddress || '',
    role: company?.role || CompanyRoleType.Contractor,
    directorEmployeeId: company?.directorEmployeeId,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let response;
      if (company) {
        // Обновление
        response = await CompanyService.update(company.id, { ...formData, id: company.id });
      } else {
        // Создание
        response = await CompanyService.create(formData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || 'Ошибка сохранения');
      }
    } catch (err) {
      setError('Произошла ошибка при сохранении');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'role' ? Number(value) : value
    }));
  };

  const handleDirectorChange = (employeeId: number | null) => {
    setFormData(prev => ({
      ...prev,
      directorEmployeeId: employeeId || undefined
    }));
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{ maxWidth: '800px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card" style={{ margin: 0, boxShadow: 'none' }}>
          <h3>{company ? 'Редактирование контрагента' : 'Создание контрагента'}</h3>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Полное название *</label>
            <input
              type="text"
              name="fullName"
              className="form-input"
              value={formData.fullName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Краткое название *</label>
            <input
              type="text"
              name="shortName"
              className="form-input"
              value={formData.shortName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">ИНН *</label>
            <input
              type="text"
              name="inn"
              className="form-input"
              value={formData.inn}
              onChange={handleChange}
              required
              pattern="\d{10,12}"
              title="ИНН должен содержать 10 или 12 цифр"
            />
          </div>

          <div className="form-group">
            <label className="form-label">ОГРН *</label>
            <input
              type="text"
              name="ogrn"
              className="form-input"
              value={formData.ogrn}
              onChange={handleChange}
              required
              pattern="\d{13,15}"
              title="ОГРН должен содержать 13 или 15 цифр"
            />
          </div>

          <div className="form-group">
            <label className="form-label">КПП *</label>
            <input
              type="text"
              name="kpp"
              className="form-input"
              value={formData.kpp}
              onChange={handleChange}
              required
              pattern="\d{9}"
              title="КПП должен содержать 9 цифр"
            />
          </div>

        <div className="form-group">
          <label className="form-label">Юридический адрес *</label>
          <textarea
            name="legalAddress"
            className="form-input"
            value={formData.legalAddress}
            onChange={handleChange}
            required
            rows={2}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Фактический адрес *</label>
          <textarea
            name="actualAddress"
            className="form-input"
            value={formData.actualAddress}
            onChange={handleChange}
            required
            rows={2}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Роль контрагента *</label>
          <select
            name="role"
            className="form-input"
            value={formData.role}
            onChange={handleChange}
            required
          >
            <option value={CompanyRoleType.Contractor}>Контрагент</option>
            <option value={CompanyRoleType.Customer}>Заказчик</option>
            <option value={CompanyRoleType.Both}>Контрагент и Заказчик</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Директор компании</label>
          <EmployeeAutocomplete
            value={formData.directorEmployeeId}
            onChange={handleDirectorChange}
            placeholder="Выберите директора компании"
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Сохранение...' : (company ? 'Сохранить' : 'Создать')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Отмена
          </button>
        </div>
      </form>
        </div>
      </div>
    </div>
  );
};

